import { SecurityOutlined } from '@mui/icons-material';
import { CircularProgress, Tooltip } from '@mui/material';
import { AxiosError } from 'axios';
import { findIndex } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import { p2pProvidersApi } from 'api';
import {
  CommissionInfo,
  CrudPage,
  CrudTableActionType,
  DataGridColumnDefinition,
  IntervalCommissionsInfoDialog,
  SidebarLayout,
  dataGridColumns,
  SidebarSize,
  P2PProviderNameInfo,
  Money,
  StylizedNumber,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { FilterDefinitionType, P2PProviderType, QueryKey } from 'enums';
import {
  useMutation,
  useP2PProvidersConfig,
  usePartialQuery,
  useUser,
  useMeta,
  useProviderTypes,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  IntervalCommission,
  P2PProvider,
  P2PProviderLastOrderData,
  P2PProvidersFilters,
} from 'types';
import { formatUtils, p2pProviderUtils, filtersUtils } from 'utils';

import { P2PProviderDetails } from './P2PProviderDetails';
import { P2P_PROVIDER_TYPES_WITH_NO_PAYOUT_PROTECTION } from './P2PProviderDetailsPage/constants';
import { protectionStatusColor } from './P2PProviderDetailsPage/utils';
import { P2PProviderLastOrder } from './P2PProviderLastOrder';

type Props = {
  archived?: boolean;
};

export const P2PProvidersPage: React.FC<Props> = ({ archived }) => {
  const { isAdmin, isTechOperator } = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });

  const { t: tCommon } = useTranslation();

  const [selectedP2PProvider, setSelectedP2PProvider] =
    useState<P2PProvider | null>(null);

  const [lastOrderData, setLastOrderData] = useState<{
    [id: string]: P2PProviderLastOrderData;
  }>({});
  const [
    intervalCommissionsInfoDialogProps,
    setIntervalCommissionsInfoDialogOpen,
  ] = useState<{
    open: boolean;
    intervalCommissions?: IntervalCommission[];
  }>({ open: false });
  const [loadingProviderIds, setLoadingProviderIds] = useState<Set<string>>(
    new Set(),
  );

  const { mutate: loadLastOrder } = useMutation(
    p2pProvidersApi.getLastTakenOrderAt,
    {
      onMutate: (itemId: string) => {
        setLoadingProviderIds((prev) => new Set([...Array.from(prev), itemId]));
      },
      onSuccess: (data) => {
        setLastOrderData((prev) => ({
          ...prev,
          [data.id]: data,
        }));
      },
      onSettled: (data, error, itemId: string) => {
        setLoadingProviderIds((prev) => {
          const next = new Set(prev);
          next.delete(itemId);
          return next;
        });
      },
      notifierType: 'execute',
    },
  );

  const queryResult = usePartialQuery(
    archived ? QueryKey.P2PProvidersArchive : QueryKey.P2PProviders,
    archived
      ? p2pProvidersApi.getAllArchivePaginated
      : p2pProvidersApi.getAllPaginated,
  );

  const { providerTypeOptions } = useProviderTypes();
  const { availableP2PProviderTypeOptions } = useP2PProvidersConfig();
  const { isDynamicAdaptersEnabled, isProviderIntervalCommissionsEnabled } =
    useMeta();

  const typeFilterOptions = useMemo(
    () =>
      isDynamicAdaptersEnabled
        ? availableP2PProviderTypeOptions
        : availableP2PProviderTypeOptions.filter((opt) => opt.isSystem),
    [isDynamicAdaptersEnabled, availableP2PProviderTypeOptions],
  );

  const getProviderTypeLabel = useCallback(
    (code: string) => {
      const option = providerTypeOptions.find((opt) => opt.value === code);
      return option?.label || p2pProviderUtils.getTypeLabel(code);
    },
    [providerTypeOptions],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<P2PProvider>[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.name'),
        valueGetter: (item) => <P2PProviderNameInfo provider={item} />,
      },
      {
        header: t('fields.type'),
        valueGetter: (item) => getProviderTypeLabel(item.type),
      },
      {
        header: t('fields.enabled'),
        valueGetter: (item) => {
          const payinEnabled = formatUtils.formatBoolean(
            item.payinConfig?.enabled,
          );
          const payoutEnabled = formatUtils.formatBoolean(
            item.payoutConfig?.enabled,
          );
          const protectionStatus =
            !P2P_PROVIDER_TYPES_WITH_NO_PAYOUT_PROTECTION.includes(
              item.type as P2PProviderType,
            ) && item.payoutConfig?.enabled
              ? item.payoutConfig?.payoutProtectionStatus
              : undefined;
          return (
            <div className="tw-inline-grid tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-2">
              <div>{tCommon('common.payin')}</div>
              <div>{payinEnabled}</div>
              <div className="tw-flex tw-items-center tw-gap-1">
                {tCommon('common.payout')}
              </div>
              <div className="tw-flex tw-items-center tw-gap-1">
                {payoutEnabled}
                {protectionStatus && (
                  <Tooltip
                    title={String(
                      t(`fields.protection_status.${protectionStatus}`),
                    )}
                  >
                    <SecurityOutlined
                      fontSize="small"
                      color={protectionStatusColor[protectionStatus]}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          );
        },
        valueClassName: 'tw-min-w-[160px]',
      },
      {
        header: (
          <Fragment>
            <div>{t('fields.expected_fee')}</div>
            <div>{`(${tCommon('common.payin')} / ${tCommon(
              'common.payout',
            )})`}</div>
          </Fragment>
        ),
        valueGetter: (item) => {
          const intervalCommissions = isProviderIntervalCommissionsEnabled
            ? item.payinConfig?.intervalCommissions
            : undefined;
          return (
            <CommissionInfo
              commission={item.payinConfig?.expectedFee}
              payoutCommission={item.payoutConfig?.expectedFee}
              intervalCommissions={intervalCommissions}
              onIntervalCommissionsButtonClick={(e: React.SyntheticEvent) => {
                e.stopPropagation();
                setIntervalCommissionsInfoDialogOpen({
                  open: true,
                  intervalCommissions,
                });
              }}
            />
          );
        },
      },
      {
        header: t('fields.last_taken_order_at'),
        valueGetter: (item) => {
          if (loadingProviderIds.has(item.id)) {
            return <CircularProgress size={20} />;
          }

          return (
            <P2PProviderLastOrder
              data={lastOrderData[item.id]}
              onLoad={() => loadLastOrder(item.id)}
            />
          );
        },
        valueClassName: 'tw-min-w-[200px]',
      },
      {
        header: t('details.payout_limits.title'),
        valueGetter: (item) => {
          const limits = item.payoutLimits;
          return limits ? (
            <div className="tw-inline-grid tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-2">
              <div className="tw-mr-2">{t('details.payout_limits.sum')}</div>
              <Money
                symbol
                value={limits?.limitSum || 0}
                fiatCurrencyId={item.fiatCurrencyId}
              />
              <div className="tw-mr-2">{t('details.payout_limits.count')}</div>
              <StylizedNumber value={limits?.limitCount || 0} />
            </div>
          ) : null;
        },
        valueClassName: 'tw-min-w-[200px]',
      },
    ],
    [
      t,
      tCommon,
      lastOrderData,
      loadingProviderIds,
      loadLastOrder,
      getProviderTypeLabel,
      isProviderIntervalCommissionsEnabled,
    ],
  );

  const { mutate: archive } = useMutation<P2PProvider, AxiosError, string>(
    p2pProvidersApi.archive,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.P2PProviders);
      },
      notifierType: 'remove',
    },
  );

  const handleSelectP2PProvider = useCallback((p2pProvider: P2PProvider) => {
    setSelectedP2PProvider(p2pProvider);
  }, []);

  const handleDeselectP2PProvider = useCallback(() => {
    setSelectedP2PProvider(null);
  }, []);

  const selectedRowIndex = useMemo(
    () =>
      findIndex(queryResult?.data?.items, {
        id: selectedP2PProvider?.id,
      }),
    [queryResult, selectedP2PProvider],
  );

  const detailsPath = useMemo(() => {
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.P2P_PROVIDERS_DETAILS;
    } else if (isAdmin) {
      return ROUTE_PATH.ADMIN.P2P_PROVIDERS_DETAILS;
    }
  }, [isTechOperator, isAdmin]);

  const handleCreate = useCallback(() => {
    navigate(generatePath(detailsPath!, { id: NEW_ID }));
  }, [detailsPath, navigate]);

  const filtersDefinitions: FilterDefinition<P2PProvidersFilters>[] = useMemo(
    () => [
      {
        label: tCommon('filters.id'),
        name: 'id',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
      {
        label: tCommon('filters.name'),
        name: 'name',
        type: FilterDefinitionType.Text,
      },
      {
        label: tCommon('filters.type'),
        name: 'type',
        type: FilterDefinitionType.Select,
        options: typeFilterOptions,
        getDisplayName: getProviderTypeLabel,
      },
      filtersUtils.getEnabledStatusFilter<P2PProvidersFilters>(
        tCommon('filters.payin_status'),
        'payinEnabled',
      ),
      filtersUtils.getEnabledStatusFilter<P2PProvidersFilters>(
        tCommon('filters.payout_status'),
        'payoutEnabled',
      ),
    ],
    [tCommon, typeFilterOptions, getProviderTypeLabel],
  );

  return (
    <SidebarLayout
      sidebarProps={{
        open: !!selectedP2PProvider,
        title: t('details.title'),
        size: SidebarSize.md,
        onClose: handleDeselectP2PProvider,
        content: selectedP2PProvider && (
          <P2PProviderDetails p2pProvider={selectedP2PProvider} />
        ),
      }}
    >
      <CrudPage
        header={
          archived
            ? undefined
            : {
                ...(!isDynamicAdaptersEnabled && { title: t('title') }),
                divider: !isDynamicAdaptersEnabled,
                ...(!isDynamicAdaptersEnabled &&
                  (isAdmin || isTechOperator) && {
                    rightContentButton: { onClick: handleCreate },
                  }),
              }
        }
        filters={{ filtersDefinitions }}
        table={{
          queryResult,
          columns,
          paginated: true,
          actions: [
            {
              type: CrudTableActionType.Details,
              path: detailsPath,
            },
            {
              type: CrudTableActionType.Remove,
              onRemove: (item, { close }) =>
                archive(item.id, { onSuccess: close }),
            },
          ],
          onRowClick: handleSelectP2PProvider,
          selectedRowIndex,
          hideActions: archived,
        }}
      />
      <IntervalCommissionsInfoDialog
        {...intervalCommissionsInfoDialogProps}
        onClose={() => setIntervalCommissionsInfoDialogOpen({ open: false })}
      />
    </SidebarLayout>
  );
};
